/**
 * Gift a Moment — PUBLIC recipient landing page.
 *
 * URL: /gift/:gift_id?ref={sender_referral_code}&source=gift_moment
 *
 * No auth required. After the audio finishes (or at any time), the
 * recipient sees a gentle CTA to "Create Your Own" that preserves the
 * original sender's referral code for attribution.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Loader2, Heart, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const VIOLET = "#8B7ED4";
const GOLD = "#CBA153";

export default function GiftViewPage() {
  const { gift_id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);

  const [gift, setGift]       = useState(null);
  const [error, setError]     = useState(null);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded]     = useState(false);

  useEffect(() => {
    fetch(`${API}/api/gift/public/${gift_id}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setGift)
      .catch(() => setError("This moment couldn't be found."));
  }, [gift_id]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else {
      audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const signupRef = gift?.sender_referral_code || params.get("ref") || "";
  const signupHref = `/signup?ref=${encodeURIComponent(signupRef)}&source=gift_moment`;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0a12", color: "rgba(245,240,230,0.8)" }}>
        <div className="text-center px-8">
          <p className="text-sm italic" style={{ fontFamily: "'Spectral', serif", color: "rgba(220,210,230,0.6)" }}>
            {error}
          </p>
          <button onClick={() => navigate("/")} className="mt-6 text-xs uppercase tracking-[0.32em]" style={{ color: VIOLET }}>
            Reclaim
          </button>
        </div>
      </div>
    );
  }

  if (!gift) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0a12" }}>
        <Loader2 size={28} className="animate-spin" color={VIOLET} />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#0a0a12", color: "rgba(245,240,230,0.9)" }} data-testid="gift-view-page">
      {/* Optional sender-uploaded cover — becomes a soft, heavily-blurred
          backdrop that makes the page feel like a handwritten card. */}
      {gift.cover_image_url && (
        <>
          <motion.img
            key="gift-cover-bg"
            src={`${API}${gift.cover_image_url}`}
            alt=""
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ filter: "blur(42px) saturate(1.05)", transform: "scale(1.25)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.55 }}
            transition={{ duration: 2.2 }}
            data-testid="gift-cover-bg"
          />
          <div className="absolute inset-0 pointer-events-none"
               style={{ background: "linear-gradient(180deg, rgba(10,10,18,0.55) 0%, rgba(10,10,18,0.82) 60%, rgba(10,10,18,0.96) 100%)" }} />
        </>
      )}

      {/* Ambient glows */}
      <motion.div className="absolute -top-40 -right-20 w-[640px] h-[640px] rounded-full pointer-events-none"
                  style={{ background: `radial-gradient(circle, ${VIOLET}22 0%, transparent 65%)`, filter: "blur(90px)" }}
                  animate={{ opacity: [0.35, 0.6, 0.35] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="absolute -bottom-40 -left-20 w-[640px] h-[640px] rounded-full pointer-events-none"
                  style={{ background: `radial-gradient(circle, ${GOLD}18 0%, transparent 65%)`, filter: "blur(90px)" }}
                  animate={{ opacity: [0.25, 0.5, 0.25] }} transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }} />

      <div className="relative z-10 max-w-xl mx-auto px-6 pt-14 pb-24 text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }}
                  className="text-[10px] uppercase tracking-[0.42em] mb-5" style={{ color: `${VIOLET}bb` }}>
          ✦ A Reclaim Moment
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.15 }}
                   className="text-3xl md:text-4xl mb-3"
                   style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.95)" }}>
          A moment was created for you,<br/><span style={{ color: GOLD }}>{gift.recipient_name}</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.35 }}
                  className="text-sm italic mt-4" style={{ fontFamily: "'Spectral', serif", color: "rgba(220,210,230,0.65)" }}>
          From {gift.sender_name}
        </motion.p>

        {/* Personal line quote block */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.55 }}
                    className="mt-12 mx-auto max-w-md rounded-3xl border px-6 py-7"
                    style={{ background: "rgba(255,255,255,0.03)", borderColor: `${VIOLET}2a` }}>
          <p className="text-xs uppercase tracking-[0.32em] mb-3" style={{ color: `${VIOLET}bb` }}>They wanted you to know</p>
          <p className="text-lg italic" style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.92)" }}>
            &ldquo;{gift.personal_line}&rdquo;
          </p>
        </motion.div>

        {/* Audio player */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.75 }}
                    className="mt-10">
          {gift.audio_url ? (
            <>
              <audio
                ref={audioRef}
                src={`${API}${gift.audio_url}`}
                onEnded={() => { setPlaying(false); setEnded(true); }}
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                data-testid="gift-audio"
              />
              <button onClick={toggle}
                      data-testid="gift-play-btn"
                      className="mx-auto flex items-center justify-center rounded-full transition-all"
                      style={{
                        width: 96, height: 96,
                        background: `linear-gradient(135deg, ${VIOLET} 0%, #6A5EB0 100%)`,
                        boxShadow: playing ? `0 0 60px ${VIOLET}88` : `0 0 40px ${VIOLET}55`,
                      }}>
                {playing ? <Pause size={36} color="white" /> : <Play size={36} color="white" className="ml-1.5" />}
              </button>
              <p className="mt-5 text-xs uppercase tracking-[0.3em]" style={{ color: `${VIOLET}bb` }}>
                {playing ? "Listening…" : "Play Your Moment"}
              </p>
            </>
          ) : (
            <div className="inline-flex items-center gap-2 text-sm" style={{ color: "rgba(220,210,230,0.65)", fontFamily: "'Spectral', serif" }}>
              <Loader2 size={16} className="animate-spin" color={VIOLET} />
              Audio is being composed — try refreshing in a moment.
            </div>
          )}
        </motion.div>

        {/* Affirmations — gentle reveal */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }}
                    className="mt-12 mx-auto max-w-md">
          <p className="text-xs uppercase tracking-[0.32em] mb-4" style={{ color: `${VIOLET}bb` }}>
            {gift.intention_label ? `Intention · ${gift.intention_label}` : "Affirmations"}
          </p>
          <ul className="space-y-3">
            {(gift.affirmations || []).map((a, i) => (
              <li key={i} className="text-lg"
                  style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.9)" }}>
                {a}
              </li>
            ))}
          </ul>
        </motion.div>

        {/* CTA — gentle, invitational */}
        <AnimatePresence>
          {(ended || playing) && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 1.4, delay: ended ? 0 : 6 }}
              className="mt-16 mx-auto max-w-md"
              data-testid="gift-recipient-cta"
            >
              <p className="text-sm italic mb-4" style={{ fontFamily: "'Spectral', serif", color: "rgba(220,210,230,0.6)" }}>
                If this moved you — you can do the same for someone you love.
              </p>
              <a href={signupHref}
                 data-testid="gift-create-your-own"
                 className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm uppercase tracking-[0.3em]"
                 style={{ background: `linear-gradient(135deg, ${VIOLET} 0%, #6A5EB0 100%)`, color: "white",
                          fontFamily: "'Manrope', sans-serif", fontWeight: 500,
                          boxShadow: "0 8px 40px rgba(139,126,212,0.3)" }}>
                <Sparkles size={15} /> Create Your Own
              </a>
              <p className="mt-3 text-[10px] uppercase tracking-[0.3em]" style={{ color: "rgba(200,190,220,0.4)" }}>
                reclaim · a quiet gift
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
